/* eslint-disable max-lines-per-function */
import { updateBy } from '@toggle/helpers';
import { Attachment, ConversationStatus } from '@toggle/toggle';

import { create } from '~/utils/store/createStore';

import {
  abortRequest,
  deleteConversation,
  getAllConversations,
  getConversationMessageWithSteps,
  getConversationTasks,
  getConversationTaskStates,
  postContinueConversation,
  postConversation,
  postFeedback,
  SubmittedAnalysisMode,
  updateConversation,
  WebSearch,
} from '../services/conversations-service';
import { useUserUpload } from '../useUserUpload/useUserUpload';
import { createActiveConversation } from '../utils/createActiveConversation';
import { ChatStore, Conversation, ConversationTask } from './use-chat.types';

const EXCEED_LIMIT_ERROR_STATUS = 429;

export const useChat = create<ChatStore>((set, get) => ({
  conversations: [],
  controller: new AbortController(),
  exceedLimit: false,
  analysisMode: 'Auto',
  webSearch: 'open',
  setWebSearch: (webSearch: WebSearch) => {
    set({ webSearch });
  },
  setAnalysisMode: (analysisMode: SubmittedAnalysisMode) => {
    set({ analysisMode });
  },
  clearActiveConversation: () => {
    set({
      conversations: get().conversations.map(c => ({
        ...c,
        active: false,
      })),
    });
  },
  clonePublicConversation: async (conversationId: string) => {
    const abortController = new AbortController();

    const conversation = await createActiveConversation({
      conversationId,
      signal: abortController.signal,
    });

    return conversation;
  },
  updateActiveConversation: ({ conversationId, taskId, steps }) => {
    set({
      conversations: get().conversations.map(c => {
        if (c.id === conversationId) {
          return {
            ...c,
            tasks: c.tasks
              ? updateBy(c.tasks, { request_id: taskId }, t => ({
                  ...t,
                  states: steps,
                }))
              : undefined,
          };
        }

        return c;
      }),
    });
  },
  abortRequest: (conversationId: string) => {
    const { controller, conversations } = get();

    const conversation = conversationId
      ? conversations.find(c => c.id === conversationId)
      : undefined;

    abortRequest(conversationId);

    const updateConversations: Conversation[] = conversation
      ? updateBy(conversations, { id: conversation.id }, c => ({
          ...c,
          tasks: c.tasks
            ? [
                ...c.tasks.slice(0, -1),
                ...(c.tasks && c.tasks.length > 0
                  ? [{ ...c.tasks[c.tasks.length - 1] }]
                  : []),
              ]
            : undefined,
        }))
      : conversations;
    set({ conversations: updateConversations });
    controller.abort();
  },
  addFeedback: ({ conversationId, requestId, feedback }) => {
    const { conversations } = get();
    postFeedback({
      conversation_id: conversationId as string,
      request_id: requestId,
      feedback,
    });

    const newConversations = updateBy(
      conversations,
      { id: conversationId },
      c => ({
        ...c,
        tasks: c.tasks
          ? updateBy(c.tasks, { request_id: requestId }, t => ({
              ...t,
              feedback,
            }))
          : undefined,
      })
    );

    set({ conversations: newConversations });
  },
  postQuestion: async (question, conversationId) => {
    set({ exceedLimit: false });

    // TODO: separate function for follow up question and new conversation
    const controller = new AbortController();
    set({ controller });

    const { analysisMode, webSearch } = get();

    const { files, removeAllFiles } = useUserUpload.getState();
    const attachments = files.flatMap(file =>
      file.status === 'complete' && file.id ? [file.id] : []
    );
    const tempAttachments: Attachment[] = files.map(file => ({
      file_id: file.field_name,
      file_name: file.name,
      size: file.sizeInBytes,
      content_type: file.fileType,
    }));

    const basePayload = {
      message: question,
      controller,
      analysis_mode: analysisMode,
      tool_config: {
        modes: {
          web_search: webSearch,
        },
      },
      attachments,
    };

    // follow up question
    if (conversationId) {
      const response = await postContinueConversation({
        conversationId,
        ...basePayload,
      });

      if (response.error?.status === EXCEED_LIMIT_ERROR_STATUS) {
        set({ exceedLimit: true });
        return undefined;
      }

      if (response.data) {
        const responseStates = await getConversationTaskStates({
          conversationId,
          taskRequestId: response.data.request_id,
        });
        removeAllFiles();
        set(state => ({
          conversations: updateBy(
            state.conversations,
            { id: conversationId },
            c => ({
              ...c,
              active: true,
              tasks: c.tasks
                ? [
                    ...c.tasks,
                    {
                      request_id: response.data.request_id,
                      start_time: new Date().toISOString(),
                      first_state: responseStates.data?.states?.[0],
                      input: question,
                      states: responseStates.data?.states || [],
                      analysis_mode:
                        responseStates.data?.states?.at(-1)?.analysis_mode,
                      submittedAnalysisMode: analysisMode,
                      attachments: tempAttachments,
                    },
                  ]
                : undefined,
            })
          ),
        }));
      }

      return conversationId;
    }

    // new conversation
    const response = await postConversation(basePayload);

    if (response.error?.status === EXCEED_LIMIT_ERROR_STATUS) {
      set({ exceedLimit: true });
      return undefined;
    }

    if (response.data) {
      const newConversation = await createActiveConversation({
        conversationId: response.data.conversation_id,
        signal: controller.signal,
      });

      if (newConversation) {
        // if newConversation.tasks is empty
        // (it happens if the `conversation/v2/id` is called before the code-agent has finished the first state.)
        // create a temp task to store request_id
        // in order to let web socket track progress

        const newTask: ConversationTask = {
          input: question,
          request_id: response.data.request_id,
          start_time: new Date().toISOString(),
          submittedAnalysisMode: analysisMode,
          attachments: tempAttachments,
        };

        const tasks = newConversation.tasks?.length
          ? newConversation.tasks
          : [newTask];

        const conversationWithTasks: Conversation = {
          ...newConversation,
          status: 'Processing',
          tasks,
          active: true,
        };

        const newConversations: Conversation[] = [
          conversationWithTasks,
          ...get().conversations,
        ];

        removeAllFiles();

        set({
          conversations: newConversations,
        });
      }

      return response.data.conversation_id;
    }

    return conversationId;
  },
  markConversationReadStatus: (conversationId, isRead) => {
    set({
      conversations: updateBy(
        get().conversations,
        { id: conversationId },
        c => ({ ...c, unRead: !isRead })
      ),
    });
  },
  getUnreadConversations: () => get().conversations.filter(c => c.unRead),
  getCompleteUnreadConversations: () => {
    const { getUnreadConversations } = get();
    return getUnreadConversations().filter(c => c.status === 'Done');
  },
  getErrorUnreadConversations: () => {
    const { getUnreadConversations } = get();
    return getUnreadConversations().filter(c => c.status === 'Error');
  },
  updateConversation: async ({ name, id, favourite, isPublic = false }) => {
    const res = await updateConversation({ id, name, favourite });

    if (!res.error) {
      if (!isPublic) {
        set(state => {
          const updateConversations = updateBy(
            state.conversations,
            { id },
            c => ({
              ...c,
              name: name ?? c.name,
              favourite: favourite ?? c.favourite,
            })
          );

          return {
            conversations: updateConversations,
          };
        });
      } else {
        const newConversations = get().conversations.reduce<Conversation[]>(
          (result, curr) => {
            const changedCopied = curr.public_copies.find(c => c.id === id);

            if (changedCopied) {
              const updatedCopy = {
                ...changedCopied,
                name: name ?? changedCopied.name,
              };
              return [
                ...result,
                {
                  ...curr,
                  public_copies: updateBy(
                    curr.public_copies,
                    { id },
                    () => updatedCopy
                  ),
                },
              ];
            }

            return [...result, curr];
          },
          []
        );
        set({
          conversations: newConversations,
        });
      }
    }

    return res;
  },
  deletePrivateConversation: async id => {
    const res = await deleteConversation(id);

    if (!res.error) {
      set(state => ({
        conversations: state.conversations.filter(c => c.id !== id),
      }));
    }

    return res;
  },
  deletePublicConversation: async id => {
    const res = await deleteConversation(id);

    if (!res.error) {
      set(state => ({
        conversations: state.conversations.map(c => {
          if (c.public_copies?.some(pc => pc.id === id)) {
            return {
              ...c,
              public_copies: c.public_copies.filter(pc => pc.id !== id),
            };
          }

          return c;
        }),
      }));
    }

    return res;
  },
  getConversations: async pageParam => {
    const response = await getAllConversations(pageParam);

    if (response.conversations) {
      set(state => {
        const existingConversationIds = new Set(
          state.conversations.map(c => c.id)
        );

        const newConversationsToAdd = response.conversations.filter(
          c => !existingConversationIds.has(c.id)
        );

        return {
          conversations: [
            ...state.conversations,
            ...newConversationsToAdd.map(c => ({
              ...c,
              public_copies: c.public_copies.map(copy => ({
                ...copy,
                public_copies: [],
              })),
            })),
          ],
        };
      });
    }

    return response.conversations;
  },
  initializeActiveConversation: async conversationId => {
    const conversationsTasks = await getConversationTasks(conversationId);

    if (conversationsTasks.data) {
      const results = await Promise.all(
        conversationsTasks.data.tasks.map(async t => {
          const states = await getConversationTaskStates({
            conversationId,
            taskRequestId: t.request_id,
          });
          return {
            ...t,
            states: states.data?.states,
          };
        })
      );
      return {
        ...conversationsTasks.data,
        tasks: results,
        public_copies: [],
      };
    }

    return conversationsTasks.data;
  },
  getActiveConversation: conversationId => {
    if (conversationId === '123') {
      return;
    }

    const { conversations } = get();
    const originalConversation = conversations.find(
      c => c.id === conversationId
    );

    if (originalConversation) {
      return originalConversation;
    }

    const allPublicCopies = conversations.flatMap(c => c.public_copies || []);
    return allPublicCopies.find(pc => pc.id === conversationId);
  },
  getActiveConversationTasks: async conversationId => {
    const { conversations } = get();

    const isOriginal = conversations.some(c => c.id === conversationId);

    const updateConversationState = (tasks: ConversationTask[]) => {
      if (isOriginal) {
        return conversations.map(c => ({
          ...c,
          active: c.id === conversationId,
          tasks: c.id === conversationId ? tasks : c.tasks,
        }));
      }

      return conversations.map(c => ({
        ...c,
        active: c.id === conversationId,
        public_copies: c.public_copies.map(pc => {
          return {
            ...pc,
            active: pc.id === conversationId,
            tasks: pc.id === conversationId ? tasks : pc.tasks,
          };
        }),
      }));
    };

    const conversation =
      conversations.find(c => c.id === conversationId) ||
      conversations.find(c =>
        c.public_copies?.find(pc => pc.id === conversationId)
      );

    if (conversation?.tasks) {
      set({
        conversations: updateConversationState(conversation.tasks),
      });
    } else {
      const conversationsTasks = await getConversationTasks(conversationId);

      if (conversationsTasks.data?.tasks) {
        set({
          conversations: updateConversationState(conversationsTasks.data.tasks),
        });
      }
    }
  },
  resetStore: () => {
    set({
      conversations: [],
    });
  },
  getConversationStates: async ({ conversationId, taskId }) => {
    const response = await getConversationMessageWithSteps({
      conversationId,
      messageId: taskId,
    });

    const states = response.states;
    const lastState = states[states.length - 1];

    let status: ConversationStatus;

    if (lastState.next) {
      status = 'Processing';
    } else {
      switch (lastState.status) {
        case 'Error':
        case 'Fatal':
          status = 'Error';
          break;
        case 'Cancel':
          status = 'Cancel';
          break;
        default:
          status = 'Done';
      }
    }

    const newConversations = get().conversations.map(c => {
      if (c.id === conversationId) {
        let tasks = c.tasks;

        if (c.tasks) {
          tasks = updateBy(c.tasks, { request_id: taskId }, task => ({
            ...task,
            unRead: !lastState.next,
            last_state: lastState.next ? undefined : lastState,
            states,
            analysis_mode: lastState.analysis_mode,
          }));
        } else if (c.status === 'Processing') {
          tasks = [
            {
              request_id: taskId,
              start_time: new Date().toISOString(),
              first_state: states[0],
              input: c.name,
              states,
              analysis_mode: lastState.analysis_mode,
            },
          ];
        }

        return {
          ...c,
          status,
          tasks,
        };
      } else if (c.public_copies?.some(pc => pc.id === conversationId)) {
        return {
          ...c,
          status,
          public_copies: updateBy(
            c.public_copies,
            { id: conversationId },
            pc => ({
              ...pc,
              tasks: pc.tasks
                ? updateBy(pc.tasks, { request_id: taskId }, m => ({
                    ...m,
                    last_state: lastState.next ? undefined : lastState,
                    states: states,
                    analysis_mode: lastState.analysis_mode,
                  }))
                : undefined,
            })
          ),
        };
      }

      return c;
    });

    set({
      conversations: newConversations,
    });

    return states;
  },
  appendPublicCopy: async ({ originId, copyId }) => {
    const res = await getConversationTasks(copyId);

    if (res.data) {
      const { data } = res;
      set(state => {
        return {
          conversations: updateBy(state.conversations, { id: originId }, c => ({
            ...c,
            public_copies: [
              {
                id: data.id,
                name: data.name,
                date: data.date,
                created_date: data.created_date,
                summary: data.summary,
                favourite: data.favourite,
                unRead: false,
                access_level: data.access_level,
                public_copies: [],
              },
              ...c.public_copies,
            ],
          })),
        };
      });
    }
  },
  appendNewConversation: (newConversation: Conversation) => {
    set(state => ({
      conversations: [newConversation, ...state.conversations],
    }));
  },
}));
